// Path: src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { clearLeadsReferenceDataCache } from '../services/common';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [available_credits, setAvailableCredits] = useState(null);

  // Load user data from localStorage on initial render
  useEffect(() => {
    const loadUserFromStorage = async () => {
      setLoading(true);
      try {
        // Try to get user data from localStorage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('access_token');
        
        if (storedUser && token) {
          // Set the user from localStorage
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Token verification removed - relying on localStorage data only
        }
      } catch (err) {
        console.error('Error loading user:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      const response = await api.post('/auth/login/', credentials);
      const { access_token, user, redirect_url, account_id, user_exist, account_status, message } = response.data;

      // Handle successful login (200 OK)
      if (response.status === 200) {
        // Store token and user data in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('user', JSON.stringify(user));

        // Update state
        setUser(user);
        setError(null);

        return { 
          success: true, 
          user, 
          access_token, 
          redirect_url: redirect_url || 'dashboard',
          account_id 
        };
      }

      // Handle other status codes
      return { 
        success: false, 
        error: 'Unexpected response from server' 
      };

    } catch (err) {
      // Handle 403 FORBIDDEN responses (redirect scenarios)
      if (err.response && err.response.status === 403) {
        const { access_token, user, redirect_url, account_id, user_exist, account_status, message } = err.response.data;
        
        // For complete_onboarding case, we still get a token but no user data
        if (redirect_url === 'complete_onboarding' && access_token) {
          localStorage.setItem('access_token', access_token);
          return { 
            success: true, 
            user: null, 
            access_token, 
            redirect_url: 'complete_onboarding',
            account_id: null 
          };
        }

        // For account_status case
        if (redirect_url === 'account_status') {
          return { 
            success: true, 
            user: null, 
            access_token: null, 
            redirect_url: 'account_status',
            account_id: account_id,
            message: message
          };
        }

        // For agent_login case
        if (redirect_url === 'app_download') {
          return { 
            success: true, 
            user: null, 
            access_token: null, 
            redirect_url: 'app_download',
            account_id: account_id,
            message: message
          };
        }

        // Other 403 cases
        return { 
          success: false, 
          error: message || 'Access denied' 
        };
      }

      // Handle other errors (400, 404, 500, etc.)
      const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.response?.data || 'Invalid credentials';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Inside AuthContext.js
const signup = async (userData) => {
  try {
    // Return raw Axios response directly
    return await api.post('/auth/signup/', userData);
  } catch (err) {
    // Throw raw Axios error so the caller can handle field-specific errors
    throw err;
  }
};


  // Logout function
  const logout = async () => {
    try {
      await api.post('/auth/logout/');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local storage and state
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setUser(null);
      clearLeadsReferenceDataCache();
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Default permissions structure aligned with backend's permissions_utils.py
  const getDefaultPermissions = (role) => {
    // This matches the backend permissions_utils.py structure
    if (role === 'AD') {
      return {
        dashboard: true,
        leads: true,
        calls: true,
        site_visits: true,
        tasks: true,
        calendar: true,
        bookings: true,
        properties: true,
        channel_partners: true,
        settings: true
      };
    } else if (role === 'MG') {
      return {
        dashboard: true,
        leads: true,
        calls: true,
        site_visits: true,
        tasks: true,
        calendar: true,
        bookings: true,
        properties: false,
        channel_partners: false,
        settings: false
      };
    } else {
      // Default or Agent role
      return {
        dashboard: true,
        leads: true,
        calls: true,
        app_download: true
      };
    }
  };

  // Oauth Login function
  const oauthLogin = async (userData, accessToken) => {
    try {
      // Ensure role permissions are properly set
      if (userData && !userData.role_permissions) {
        // If permissions aren't set from backend, set them based on role
        userData.role_permissions = getDefaultPermissions(userData.role);
      }
      
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      console.log('OAuth login successful with role permissions:', userData.role_permissions);
    } catch (err) {
      console.error('Login failed', err);
    }
  };


    // Helper function to check if user has permission for a specific feature
  const hasPermission = (feature) => {
    if (!user || !user.role_permissions) return false;
    return user.role_permissions[feature] === true;
  };

  // Value object to provide through context
  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    available_credits,
    oauthLogin,
    signup,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};